/**
 * Shared auth-provider sign-in helpers + a consolidated Hebrew error mapper.
 *
 * The three office apps (elka-manager, crm, quotes) offer Google + Microsoft
 * (Azure AD) sign-in. The team PWA stays on phone auth and does not use these.
 */

import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  type Auth,
  type UserCredential,
} from 'firebase/auth';

/** Google sign-in with a forced account chooser (matches existing app behavior). */
export function signInWithGoogle(auth: Auth): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

/** Microsoft (Azure AD) sign-in — multi-tenant ('common') with a forced account chooser. */
export function signInWithMicrosoft(auth: Auth): Promise<UserCredential> {
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ tenant: 'common', prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

/**
 * Map a Firebase auth error (or anything thrown during sign-in) to a Hebrew
 * message. Consolidates the per-app login-page mappings into one place.
 */
export function loginErrorMessage(err: unknown): string {
  const authErr = err as {
    code?: string;
    message?: string;
    customData?: { email?: string };
  };

  switch (authErr.code) {
    case 'auth/popup-closed-by-user':
      return 'החלון נסגר. נסה שוב.';
    case 'auth/cancelled-popup-request':
      return 'הבקשה בוטלה. נסה שוב.';
    case 'auth/popup-blocked':
      return 'הדפדפן חסם את חלון ההתחברות. אנא אפשר חלונות קופצים ונסה שוב.';
    case 'auth/network-request-failed':
      return 'בעיית רשת. בדוק את החיבור לאינטרנט ונסה שוב.';
    case 'auth/internal-error':
      return 'שגיאה פנימית. נסה שוב.';
    case 'auth/account-exists-with-different-credential': {
      const email = authErr.customData?.email;
      return email
        ? `כתובת האימייל ${email} כבר רשומה עם ספק התחברות אחר. התחבר עם Google.`
        : 'כתובת אימייל זו כבר רשומה עם ספק התחברות אחר. התחבר עם Google.';
    }
    default:
      return authErr.message || 'שגיאה בהתחברות';
  }
}
